import { Dispatch, SetStateAction, useContext, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { CheckCircleOutlined } from '@mui/icons-material'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Alert, AlertTitle, Button, CircularProgress, DialogActions, DialogContent, Grid, Link, Typography } from '@mui/material'

import DialogWithTitle from '../../../../../components/dialog-with-title'
import { AppContext, useHost } from '../../../../../providers/app-provider'
import Api from '../../../../../utils/api'
import useStyles from '../index.styles'

interface IProps {
  open: boolean
  setOpen: Dispatch<SetStateAction<boolean>>
  folderId: number
  folderName: string
}

const ReactivateFolderModal = (props: IProps) => {
  const { open, setOpen, folderId, folderName } = props

  const classes = useStyles
  const auth = useAuth()
  const host = useHost()
  const { refreshPage } = useContext(AppContext)

  const [reactivationInitiated, setReactivationInitiated] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!host || !auth.user?.access_token) return
    setIsSubmitting(true)
    setReactivationInitiated(true)
    const res = await Api.post(`${host}/api/v2/central/folders/${folderId}/reactivate`, {})
    if (!res.ok) {
      setError(res.body.message)
    }
    setIsSubmitting(false)
  }

  const handleDone = () => {
    setOpen(false)
    setReactivationInitiated(false)
    setError(null)

    if (!error) refreshPage()
  }

  return (
    <DialogWithTitle setOpen={setOpen} open={open} title="Reactivation" sx={classes.dialog}>
      <DialogContent>
        {!reactivationInitiated ? (
          <Grid container spacing={2}>
            <Grid size={12}>
              <Typography variant="subtitle2">You have selected this folder to reactivate:</Typography>
              <Typography variant="body1">{folderName}</Typography>
            </Grid>

            <Grid size={12}>
              <Alert severity="warning">
                <AlertTitle>Notice:</AlertTitle>
                Reactivating the folder will set account to active, restart billing to the client, and reinstate all users and devices.
              </Alert>
            </Grid>
          </Grid>
        ) : (
          <Grid container={true} spacing={2}>
            {isSubmitting && (
              <Grid size={12}>
                <Alert severity="info" icon={<CircularProgress size={22} />}>
                  <AlertTitle sx={{ p: 0, m: 0 }}>Processing reactivation</AlertTitle>
                </Alert>
              </Grid>
            )}

            {error && (
              <Grid size={12}>
                <Alert severity="error">
                  <AlertTitle>Error</AlertTitle>
                  {error}. Please create a support ticket at{' '}
                  <Link href="https://support.meshify.com" target="_blank" rel="noopener">
                    support.meshify.com
                  </Link>
                  .
                </Alert>
              </Grid>
            )}

            {reactivationInitiated && !isSubmitting && !error && (
              <Grid size={12}>
                <Alert severity="success" icon={<CheckCircleOutlined />}>
                  <AlertTitle sx={{ p: 0, pt: '2px', m: 0 }}>Folder successfully reactivated</AlertTitle>
                </Alert>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogContent>
        <DialogActions>
          {!reactivationInitiated ? (
            <>
              <Button variant="outlined" onClick={() => setOpen(false)}>
                Cancel
              </Button>

              <Button variant="contained" onClick={handleSubmit} endIcon={<ArrowForwardIosIcon sx={classes.initiateIcon} />}>
                Initiate
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleDone} disabled={isSubmitting}>
              Done
            </Button>
          )}
        </DialogActions>
      </DialogContent>
    </DialogWithTitle>
  )
}

export default ReactivateFolderModal
